import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Terminal, Globe, RefreshCw, ExternalLink, AlertCircle, 
  Settings, Link, Upload, Send, User, Layers, Hash, Clock, ArrowUpRight 
} from 'lucide-react';
import PremiumCard from '../ui/PremiumCard';
import { updateAmPassportProfile } from '../../utils/profileUpdater';
import type { 
  AntOption, 
  PrimaryNameRequest, 
  StatusMessage, 
  Undernames,
  UndernameRecord 
} from '../../types';
// Updated ArnsTab interface to use the hook
interface ArnsTabProps {
  currentPrimaryName?: string | null;
  userAntProcessId?: string | null;
  primaryNameRequest?: PrimaryNameRequest | null;
  antOptions: AntOption[];
  loadingAntCheck: boolean;
  antCheckError?: string;
  checkPrimaryNameAndAnt: () => void;
  antNameInput: string;
  setAntNameInput: (value: string) => void;
  selectedAntProcessId?: string;
  setSelectedAntProcessId: (value: string) => void;
  requestPrimaryName: (params: { antName?: string; selectedAntProcessId?: string }) => Promise<void>;
  canMintPassport: boolean;
  handleUpdateAntMetadata: () => void;
  updatingAnt: boolean;
  antUpdateStatus: StatusMessage;
  undernameInput: string;
  setUndernameInput: (value: string) => void;
  handleSetUndername: () => void;
  settingUndername: boolean;
  undernameStatus: StatusMessage;
  currentAvatar?: string | null;
  avatarFile?: File | null;
  avatarPreview?: string | null;
  handleAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  uploadingAvatar: boolean;
  handleUploadAvatar: () => void;
  avatarStatus: StatusMessage;
  undernames: Undernames;
  syncAntData: () => void;
  // Additional props for profile update
  userData?: any;
  levelTitle?: string;
  level?: number;
  // For updating status
  setUpdatingAnt: (updating: boolean) => void;
  setAntUpdateStatus: (status: StatusMessage) => void;
}

const ArnsTab: React.FC<ArnsTabProps> = ({
  currentPrimaryName,
  userAntProcessId,
  primaryNameRequest,
  antOptions,
  loadingAntCheck,
  antCheckError,
  checkPrimaryNameAndAnt,
  antNameInput,
  setAntNameInput,
  selectedAntProcessId,
  setSelectedAntProcessId,
  requestPrimaryName,
  canMintPassport,
  handleUpdateAntMetadata,
  updatingAnt,
  antUpdateStatus,
  undernameInput,
  setUndernameInput,
  handleSetUndername,
  settingUndername,
  undernameStatus,
  currentAvatar,
  avatarFile,
  avatarPreview,
  handleAvatarChange,
  uploadingAvatar,
  handleUploadAvatar,
  avatarStatus,
  undernames,
  syncAntData,
  userData,
  levelTitle,
  level,
  setUpdatingAnt,
  setAntUpdateStatus
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-[#E8FDF2] mb-2">ArNS Management</h2>
        <p className="text-[#8AA5A2]">Configure your Arweave Name System and manage your digital identity</p>
      </div>

      {/* Primary Name Status */}
      <PremiumCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF4747] to-[#FF6B6B] rounded-xl flex items-center justify-center">
              <Terminal className="w-6 h-6 text-[#0A0E1B]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-[#E8FDF2]">Primary ArNS Name</h3>
              <p className="text-sm text-[#8AA5A2]">
                {currentPrimaryName ? 'Your active ArNS domain' : 'Set up your ArNS identity'}
              </p>
            </div>
          </div>
          
          <button
            onClick={checkPrimaryNameAndAnt}
            disabled={loadingAntCheck}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Refresh ANT status"
          >
            <RefreshCw className={`w-5 h-5 text-[#8AA5A2] ${loadingAntCheck ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {currentPrimaryName ? (
          <div className="space-y-4">
            <div className="p-4 bg-[#0A0E1B]/40 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-[#E8FDF2] mb-1">
                    {currentPrimaryName}
                  </div>
                  <div className="text-sm text-[#8AA5A2] font-mono">
                    Process: {userAntProcessId?.slice(0, 12)}...{userAntProcessId?.slice(-6)}
                  </div>
                </div>
                <a
                  href={`https://${currentPrimaryName}.ar.io`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#1A1F2E] hover:bg-[#242938] rounded-xl transition-all flex items-center gap-2 text-[#E8FDF2]"
                >
                  <ArrowUpRight className="w-4 h-4" />
                  Visit Site
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {primaryNameRequest && (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <div className="flex items-center gap-3 text-yellow-400">
                  <Clock className="w-5 h-5" />
                  <span>Pending request for "{primaryNameRequest.name}"</span>
                </div>
              </div>
            )}
            
            {/* ANT Selection */}
            {antOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[#E8FDF2] mb-2">
                  Select from Your ANT Tokens
                </label>
                <select
                  className="w-full px-4 py-3 bg-[#1A1F2E] border border-white/10 rounded-xl text-[#E8FDF2] 
                    focus:border-[#FF4747] focus:outline-none transition-all"
                  value={selectedAntProcessId || ''}
                  onChange={e => {
                    setSelectedAntProcessId(e.target.value);
                    const selectedOption = antOptions.find(opt => opt.processId === e.target.value);
                    setAntNameInput(selectedOption?.name || "");
                  }}
                  disabled={loadingAntCheck}
                >
                  <option value="">-- Select an ANT --</option>
                  {antOptions.map(ant => (
                    <option key={ant.processId} value={ant.processId}>
                      {ant.name} ({ant.processId.slice(0, 8)}...)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {antOptions.length > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="text-sm text-[#8AA5A2]">OR</span>
                <div className="flex-1 h-px bg-white/10"></div>
              </div>
            )}

            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-[#E8FDF2] mb-2">
                Enter ArNS Name
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-[#1A1F2E] border border-white/10 rounded-xl text-[#E8FDF2] 
                  focus:border-[#FF4747] focus:outline-none transition-all placeholder-[#5A6B68]"
                value={antNameInput}
                onChange={e => setAntNameInput(e.target.value)}
                disabled={loadingAntCheck || !!selectedAntProcessId}
                placeholder="e.g. myarnsname"
              />
            </div>

            <button
              onClick={() => requestPrimaryName({ 
                antName: antNameInput, 
                selectedAntProcessId: selectedAntProcessId 
              })}
              disabled={loadingAntCheck || (!antNameInput && !selectedAntProcessId)}
              className={`w-full px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                !loadingAntCheck && (antNameInput || selectedAntProcessId)
                  ? 'bg-gradient-to-r from-[#FF4747] to-[#FF6B6B] text-white hover:opacity-90'
                  : 'bg-[#1A1F2E] text-[#8AA5A2] cursor-not-allowed'
              }`}
            >
              <Globe className="w-5 h-5" />
              {loadingAntCheck ? "Processing..." : selectedAntProcessId ? "Request as Primary" : "Request Primary Name"}
            </button>

            {antCheckError && (
              <motion.div 
                className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-400">{antCheckError}</span>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </PremiumCard>

      {/* Management Actions */}
      {currentPrimaryName && userAntProcessId && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Metadata Update */}
          <PremiumCard className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Settings className="w-5 h-5 text-[#15FF75]" />
              <h4 className="text-lg font-semibold text-[#E8FDF2]">Update Metadata</h4>
            </div>
            
            <p className="text-sm text-[#8AA5A2] mb-4">
              Sync your profile data with your ANT metadata
            </p>
            
            <button
              onClick={handleUpdateAntMetadata}
              disabled={updatingAnt || !canMintPassport}
              className={`w-full px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                !updatingAnt && canMintPassport
                  ? 'bg-[#1A1F2E] text-[#E8FDF2] hover:bg-[#242938]'
                  : 'bg-[#1A1F2E] text-[#8AA5A2] cursor-not-allowed opacity-50'
              }`}
            >
              <Upload className="w-5 h-5" />
              {updatingAnt ? "Updating..." : "Update Metadata"}
            </button>
            
            {antUpdateStatus.message && (
              <motion.div 
                className={`mt-4 p-3 rounded-xl text-sm ${
                  antUpdateStatus.error 
                    ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
                    : 'bg-green-500/10 border border-green-500/20 text-green-400'
                }`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {antUpdateStatus.message}
              </motion.div>
            )}
          </PremiumCard>

          {/* Profile Link */}
          <PremiumCard className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Link className="w-5 h-5 text-[#00D4FF]" />
              <h4 className="text-lg font-semibold text-[#E8FDF2]">Profile Link</h4>
            </div>
            
            <p className="text-sm text-[#8AA5A2] mb-4">
              Set a custom undername for your profile
            </p>
            
            <input
              type="text"
              className="w-full px-4 py-3 bg-[#1A1F2E] border border-white/10 rounded-xl text-[#E8FDF2] 
                focus:border-[#00D4FF] focus:outline-none transition-all placeholder-[#5A6B68] mb-3"
              value={undernameInput}
              onChange={e => setUndernameInput(e.target.value)}
              disabled={settingUndername}
              placeholder="e.g. ampassport"
            />
            
            <button
              onClick={handleSetUndername}
              disabled={settingUndername || !undernameInput}
              className={`w-full px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                !settingUndername && undernameInput
                  ? 'bg-[#1A1F2E] text-[#E8FDF2] hover:bg-[#242938]'
                  : 'bg-[#1A1F2E] text-[#8AA5A2] cursor-not-allowed opacity-50'
              }`}
            >
              <Link className="w-5 h-5" />
              {settingUndername ? "Setting..." : "Set Profile Link"}
            </button>
            
            {undernameStatus.message && (
              <motion.div 
                className={`mt-4 p-3 rounded-xl text-sm ${
                  undernameStatus.error 
                    ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
                    : 'bg-green-500/10 border border-green-500/20 text-green-400'
                }`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {undernameStatus.message}
              </motion.div>
            )}
          </PremiumCard>
        </div>
      )}

      {/* Avatar Upload */}
      {currentPrimaryName && userAntProcessId && (
        <PremiumCard className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-6 h-6 text-[#2CF98E]" />
            <h3 className="text-xl font-semibold text-[#E8FDF2]">Profile Avatar</h3>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {avatarPreview || currentAvatar ? (
              <motion.img 
                src={avatarPreview || currentAvatar || ''} 
                alt="Avatar" 
                className="w-24 h-24 rounded-xl object-cover border-2 border-white/20"
                whileHover={{ scale: 1.05 }}
              />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-[#1A1F2E] flex items-center justify-center border border-white/10">
                <User className="w-12 h-12 text-[#5A6B68]" />
              </div>
            )}
            
            <div className="flex-1 space-y-3">
              <p className="text-sm text-[#8AA5A2]">
                Upload an avatar to personalize your ArNS profile
              </p>
              
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleAvatarChange}
                className="hidden"
              />
              
              <div className="flex gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="px-4 py-2 bg-[#1A1F2E] hover:bg-[#242938] rounded-lg transition-all flex items-center gap-2 text-[#E8FDF2]"
                >
                  <Upload className="w-4 h-4" />
                  Choose Image
                </button>
                
                {avatarFile && (
                  <button
                    onClick={handleUploadAvatar}
                    disabled={uploadingAvatar || !canMintPassport}
                    className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                      !uploadingAvatar && canMintPassport
                        ? 'bg-gradient-to-r from-[#2CF98E] to-[#15FF75] text-[#0A0E1B] hover:opacity-90'
                        : 'bg-[#1A1F2E] text-[#8AA5A2] cursor-not-allowed opacity-50'
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    {uploadingAvatar ? "Uploading..." : "Upload to Arweave"}
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {avatarStatus.message && (
            <motion.div 
              className={`mt-4 p-4 rounded-xl ${
                avatarStatus.error 
                  ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
                  : 'bg-green-500/10 border border-green-500/20 text-green-400'
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {avatarStatus.message}
            </motion.div>
          )}
        </PremiumCard>
      )}
      {/* Update amPassport Profile Page */}
     {/* Update amPassport Profile Page */}
      {currentPrimaryName && userAntProcessId && undernames?.ampassport && userData && (
        <PremiumCard className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <RefreshCw className="w-5 h-5 text-[#FF4747]" />
            <h4 className="text-lg font-semibold text-[#E8FDF2]">Update amPassport Page</h4>
          </div>
          
          <p className="text-sm text-[#8AA5A2] mb-4">
            Refresh your amPassport profile page with latest data and design
          </p>
          
          <button
            onClick={async () => {
              setUpdatingAnt(true);
              setAntUpdateStatus({ message: "Updating profile page...", error: false });

              const result = await updateAmPassportProfile({
                userData,
                currentPrimaryName,
                currentAvatar,
                levelTitle,
                level,
                userAntProcessId
              });

              setAntUpdateStatus({
                message: result.message,
                error: !result.success
              });

              setUpdatingAnt(false);
              
              // Refresh ArNS data to show updated transaction
              if (result.success) {
                setTimeout(() => {
                  checkPrimaryNameAndAnt;
                }, 3000);
              }
            }}
            disabled={updatingAnt}
            className={`w-full px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
              !updatingAnt
                ? 'bg-[#1A1F2E] text-[#E8FDF2] hover:bg-[#242938]'
                : 'bg-[#1A1F2E] text-[#8AA5A2] cursor-not-allowed opacity-50'
            }`}
          >
            <RefreshCw className={`w-5 h-5 ${updatingAnt ? 'animate-spin' : ''}`} />
            {updatingAnt ? "Updating..." : "Update Profile Page"}
          </button>
          
          {antUpdateStatus.message && (
            <motion.div 
              className={`mt-4 p-3 rounded-xl text-sm ${
                antUpdateStatus.error 
                  ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
                  : 'bg-green-500/10 border border-green-500/20 text-green-400'
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {antUpdateStatus.message}
            </motion.div>
          )}
        </PremiumCard>
      )}

      {/* Undernames List */}
      {Object.keys(undernames).length > 0 && (
        <PremiumCard className="p-6">
          <h3 className="text-xl font-semibold text-[#E8FDF2] mb-6 flex items-center gap-3">
            <Layers className="w-6 h-6 text-[#8B5CF6]" />
            Active Undernames
          </h3>
          
          <div className="space-y-3">
            {Object.entries(undernames).map(([name, record]: [string, UndernameRecord]) => (
              <div 
                key={name}
                className="p-4 bg-[#1A1F2E] rounded-xl flex items-center justify-between hover:bg-[#242938] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
                    <Hash className="w-5 h-5 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <div className="font-medium text-[#E8FDF2]">{name}</div>
                    <div className="text-sm text-[#8AA5A2] truncate max-w-md">
                      {record.url || `arweave.net/${record.transactionId?.slice(0, 12)}...`}
                    </div>
                  </div>
                </div>
                <a
                  href={record.url || `https://arweave.net/${record.transactionId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4 text-[#8AA5A2]" />
                </a>
              </div>
            ))}
          </div>
        </PremiumCard>
      )}

      {/* Data Sync */}
      {currentPrimaryName && userAntProcessId && (
        <PremiumCard className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-6 h-6 text-[#FFB800]" />
              <div>
                <h3 className="text-lg font-semibold text-[#E8FDF2]">Data Synchronization</h3>
                <p className="text-sm text-[#8AA5A2]">Sync your ANT data with the backend</p>
              </div>
            </div>
            
            <button
              onClick={syncAntData}
              disabled={updatingAnt}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                !updatingAnt
                  ? 'bg-[#1A1F2E] text-[#E8FDF2] hover:bg-[#242938]'
                  : 'bg-[#1A1F2E] text-[#8AA5A2] cursor-not-allowed opacity-50'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${updatingAnt ? 'animate-spin' : ''}`} />
              {updatingAnt ? "Syncing..." : "Sync Data"}
            </button>
          </div>
        </PremiumCard>
      )}
    </div>
  );
};

export default ArnsTab;